import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import blogService from '../../services/blogService';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const blogsPerPage = 10;

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      const result = await blogService.getAllBlogs();
      
      if (result.success) {
        setBlogs(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load blogs');
      console.error('Error loading blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

  const handleDelete = async (blogId) => {
    try {
      const result = await blogService.deleteBlog(blogId);
      
      if (result.success) {
        setBlogs(blogs.filter(blog => blog.id !== blogId));
        setShowDeleteModal(false);
        setBlogToDelete(null);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to delete blog');
      console.error('Error deleting blog:', err);
    }
  };

  const handleTogglePublish = async (blogId, currentStatus) => {
    try {
      const result = await blogService.togglePublishStatus(blogId);
      
      if (result.success) {
        setBlogs(blogs.map(blog => 
          blog.id === blogId 
            ? { ...blog, isPublished: !currentStatus }
            : blog
        ));
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to update blog status');
      console.error('Error updating blog status:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  // Filter and search blogs
  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'published' && blog.isPublished) ||
                         (filterStatus === 'draft' && !blog.isPublished);
    
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container-fluid py-5">
        <div className="row justify-content-center">
          <div className="col-12 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4" style={{ paddingTop: '120px' }}>
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-0" style={{ paddingTop: '115px' }}>Blog Dashboard</h1>
              <p className="text-muted">Welcome back, {user?.email}</p>
            </div>
            <div className="d-flex gap-2">
              <Link to="/admin/blog/new" className="btn btn-primary">
                <i className="bi bi-plus-lg me-1"></i>
                New Blog Post
              </Link>
              <button className="btn btn-outline-secondary" onClick={handleLogout}>
                <i className="bi bi-box-arrow-right me-1"></i>
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setError(null)}
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <i className="bi bi-journal-text text-primary fs-2"></i>
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="fw-bold fs-4">{blogs.length}</div>
                  <div className="text-muted small">Total Blogs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <i className="bi bi-eye text-success fs-2"></i>
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="fw-bold fs-4">{blogs.filter(b => b.isPublished).length}</div>
                  <div className="text-muted small">Published</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <i className="bi bi-file-earmark-text text-warning fs-2"></i>
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="fw-bold fs-4">{blogs.filter(b => !b.isPublished).length}</div>
                  <div className="text-muted small">Drafts</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <i className="bi bi-calendar3 text-info fs-2"></i>
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="fw-bold fs-4">
                    {blogs.filter(b => {
                      const today = new Date();
                      const blogDate = new Date(b.createdAt);
                      return blogDate.toDateString() === today.toDateString();
                    }).length}
                  </div>
                  <div className="text-muted small">Today</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search blogs..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={filterStatus}
                    onChange={(e) => handleStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <button 
                    className="btn btn-outline-secondary w-100"
                    onClick={loadBlogs}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blog Table */}
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              {currentBlogs.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Updated</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentBlogs.map((blog) => (
                          <tr key={blog.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                {blog.featuredImage && (
                                  <img
                                    src={blog.featuredImage}
                                    alt={blog.title}
                                    className="rounded me-2"
                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                  />
                                )}
                                <div>
                                  <div className="fw-medium">{blog.title}</div>
                                  <small className="text-muted">
                                    {blog.tags && blog.tags.slice(0, 2).map(tag => (
                                      <span key={tag} className="badge bg-light text-dark me-1">
                                        {tag}
                                      </span>
                                    ))}
                                  </small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${blog.isPublished ? 'bg-success' : 'bg-warning'}`}>
                                {blog.isPublished ? 'Published' : 'Draft'}
                              </span>
                            </td>
                            <td>{formatDate(blog.createdAt)}</td>
                            <td>{formatDate(blog.updatedAt)}</td>
                            <td>
                              <div className="btn-group btn-group-sm" role="group">
                                <Link
                                  to={`/admin/blog/edit/${blog.id}`}
                                  className="btn btn-outline-primary"
                                  title="Edit"
                                >
                                  <i className="bi bi-pencil"></i>
                                </Link>
                                <button
                                  className={`btn ${blog.isPublished ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                  onClick={() => handleTogglePublish(blog.id, blog.isPublished)}
                                  title={blog.isPublished ? 'Unpublish' : 'Publish'}
                                >
                                  <i className={`bi ${blog.isPublished ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                </button>
                                {blog.isPublished && (
                                  <Link
                                    to={`/blog/${blog.slug}`}
                                    className="btn btn-outline-info"
                                    title="View"
                                    target="_blank"
                                  >
                                    <i className="bi bi-box-arrow-up-right"></i>
                                  </Link>
                                )}
                                <button
                                  className="btn btn-outline-danger"
                                  onClick={() => {
                                    setBlogToDelete(blog);
                                    setShowDeleteModal(true);
                                  }}
                                  title="Delete"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <nav className="mt-4">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, index) => (
                          <li 
                            key={index + 1} 
                            className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(index + 1)}
                            >
                              {index + 1}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <i className="bi bi-journal-text display-1 text-muted mb-3"></i>
                  <h4>No blogs found</h4>
                  <p className="text-muted">
                    {searchTerm || filterStatus !== 'all' 
                      ? 'No blogs match your current filters'
                      : 'Get started by creating your first blog post'
                    }
                  </p>
                  <Link to="/admin/blog/new" className="btn btn-primary">
                    Create New Blog
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && blogToDelete && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setBlogToDelete(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete the blog post:</p>
                <p className="fw-bold">"{blogToDelete.title}"</p>
                <p className="text-danger small">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setBlogToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={() => handleDelete(blogToDelete.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;